import { Check, X } from 'lucide-react';
import { useFormContext } from 'react-hook-form';

import { Button, FormField, Text } from '@/_components';
import {
  CardContainer,
  FormMediaInput,
  ImageTextHeader,
  PageHeaderCtrlContainer,
  StatusButtonInput,
  useMedia,
} from '@/_containers';
import { PageHeaderContainer } from '@/_containers/headers/page/page-header-container';
import type { OnPartialUpdate } from '@/_schemas/types';
import { nameResolver } from '@/_shared';

import type { ContactUpdateSchema } from '../../schemas';

export function Header({
  onPartialUpdate,
  isPending,
  isEditable,
}: {
  isPending: boolean;
  isEditable: boolean;
  onPartialUpdate: OnPartialUpdate<ContactUpdateSchema>;
  isProfilePage?: boolean;
}) {
  const form = useFormContext();
  const [first_name, last_name, title, remote_worker, id] = form.watch([
    'first_name',
    'last_name',
    'title',
    'remote_worker',
    'id',
  ]);
  const folder_path = `contacts/${id}`;

  const handleRemoteWorkCtrl = () => {
    const r = form.getValues('remote_worker');
    const v = r === '1' ? 0 : 1;
    form.setValue('remote_worker', `${v}`);
    onPartialUpdate({ remote_worker: v }, 'Remote work status has been updated successfully');
  };

  const { onImageUpdate } = useMedia(onPartialUpdate);

  return (
    <CardContainer bodyClassName="flex">
      <PageHeaderContainer>
        <ImageTextHeader
          imageSlot={
            <FormField
              control={form.control}
              name="profile_image"
              render={({ field: { ref: _, ...field } }) => (
                <FormMediaInput
                  {...field}
                  size="lg"
                  shape="circle"
                  label=""
                  disabled={!isEditable}
                  folder_path={folder_path}
                  onImageStateChange={onImageUpdate}
                />
              )}
            />
          }
          primarySlot={nameResolver(first_name, last_name)}
          secondarySlot={
            <Text as="small" className="text-c-card-muted mt-0.5 font-normal">
              {title || ''}
            </Text>
          }
          tertiarySlot={
            <PageHeaderCtrlContainer>
              <StatusButtonInput isPending={isPending} onUpdate={onPartialUpdate} />

              <div className="border-c-border-muted mt-1 h-5 w-px border-r" />

              <Button
                as="link"
                size="xs"
                disabled={isPending}
                onClick={handleRemoteWorkCtrl}
                className="justify-start"
                icon={remote_worker === '1' ? Check : X}
                iconClassName={remote_worker === '1' ? 'text-success' : 'text-error'}
              >
                Remote Worker
              </Button>
            </PageHeaderCtrlContainer>
          }
        />
      </PageHeaderContainer>
    </CardContainer>
  );
}
